<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import RlUploadMultiple from '@/components/common/upload/RlUploadMultiple.vue'
import RlAttachmentsList from '@/components/rc/miner-repairs/repairs-sidebar/miner-sidebar-sections/AttachmentsList.vue'
import { useMinerDetails } from '@/composables/rc/miner-details'
import { useMinerRepairActions } from '@/composables/rc/miner-repair-actions'
import { toPartsArray } from '@/helpers/rc/miners'
import { DiagnosticResult } from '@/types/rc/miners'
import { PartDiagnosticStatus, PartStatus } from '@/types/rc/miners/PartStatus'
import { Icon } from '@iconify/vue'
import { useQueryClient } from '@tanstack/vue-query'
import { ElButton, type UploadFile } from 'element-plus'
import { computed, ref, toRefs } from 'vue'

const props = defineProps<{ minerId: number }>()
const { minerId } = toRefs(props)

// Server state

const queryClient = useQueryClient()

const { minerDetailsQuery } = useMinerDetails(minerId)
const { data: minerDetails } = minerDetailsQuery

const { updateDiagnosticMutation, deleteMinerDiagnosticMutation } = useMinerRepairActions(minerId)

const isDiagnosed = computed(() => !!minerDetails.value?.condition.diagnosed)
const diagnosticAttachments = computed(() => minerDetails.value?.condition.diagnostic?.files || [])
const diagnosticResult = computed(() => minerDetails.value?.condition.diagnostic?.result)
const diagnosticFailureReasons = computed(() => minerDetails.value?.condition.diagnostic?.failure_reasons)

// UI state

const selectedResult = ref<DiagnosticResult | undefined>(diagnosticResult.value ?? undefined)
const failureReasons = ref<string>()

const diagnosticFiles = ref<UploadFile[]>([])

// Validations:

const hasAllDueParts = computed(() => {
  if (!minerDetails.value) return false
  const hasPSU = !!minerDetails.value.psu
  const hasCB = !!minerDetails.value.cb
  const hasAtLeastOneHB = minerDetails.value.hashboards.length > 0
  const isHBOnly = minerDetails.value.hashboard_only
  return hasAtLeastOneHB && ((hasPSU && hasCB) || isHBOnly)
})

const hasAllPartsAreGoodOrUndiagnosed = computed(() => {
  if (!minerDetails.value) return false

  const parts = toPartsArray(minerDetails.value)
  const partStatuses = parts.map((part) => part.status)
  const partInitialDiagnostics = parts.map((part) => part.initial_diagnostic)

  const allPartsStatusesOk = partStatuses.every((status) => !status || status === PartStatus.Good)
  const allPartsInitialDiagnosticsOk = partInitialDiagnostics.every(
    (initialDiagnostic) => !initialDiagnostic || initialDiagnostic === PartDiagnosticStatus.Good
  )

  return allPartsStatusesOk && allPartsInitialDiagnosticsOk
})

const hasMacAddress = computed(() => !!minerDetails.value?.mac_address)

// New data is insufficient if...
const newDiagnosticDataValidationMessage = computed(() => {
  // ...there's no selected result...
  if (!selectedResult.value) return 'Select a diagnostic result'
  // ...or the result is pass and there's no files...
  if (selectedResult.value === DiagnosticResult.PASS && diagnosticFiles.value.length === 0)
    return 'Passing status requires attachments'
  // ...or the result hasn't changed since the latest save...
  if (selectedResult.value === diagnosticResult.value) return 'No changes to save'
  // ...or the result is fail and there's no _new_ files...
  if (
    selectedResult.value === DiagnosticResult.FAIL &&
    diagnosticAttachments.value.length > 0 &&
    diagnosticFiles.value.length === 0
  )
    return 'Add new attachments or change result to Passing to remove existing attachments'
  return undefined
})

/** Works as validation flag as well, providing the guarantee that if there's a validation error there'll be a message */
const validationMessage = computed(() => {
  if (!hasAllDueParts.value) return 'Miner is missing core parts'

  if (selectedResult.value === DiagnosticResult.PASS) {
    if (!minerDetails.value?.hashboard_only && !hasMacAddress.value) return 'Miner is missing MAC address'
    if (!hasAllPartsAreGoodOrUndiagnosed.value) return "Cannot diagnose as Passing if any part's not in Good state"
  }

  if (newDiagnosticDataValidationMessage.value) return newDiagnosticDataValidationMessage.value

  return undefined
})

// Expose the ref for testing purposes
defineExpose({ diagnosticFiles })

const reset = () => {
  queryClient.invalidateQueries({ queryKey: ['miner-details', props.minerId] })
  diagnosticFiles.value = []
  failureReasons.value = undefined
}

/* UI/server calls bindings */

const saveDiagnostic = () => {
  if (!selectedResult.value || validationMessage.value) return
  updateDiagnosticMutation.mutate(
    {
      diagnostic: {
        result: selectedResult.value,
        files: diagnosticFiles.value,
        date: new Date(),
        failure_reasons: failureReasons.value,
      },
    },
    {
      onSuccess: () => {
        reset()
      },
    }
  )
}

const { mutateAsync: deleteDiagnostic, isPending: diagnosticDeletionPending } = deleteMinerDiagnosticMutation

const clearDiagnostic = () => {
  DeleteConfirmationModal(
    'Clear Diagnostic',
    'Are you sure you want to clear the diagnostic?',
    props.minerId,
    deleteDiagnostic,
    () => {
      reset()
    }
  )
}
</script>

<template>
  <!-- Diagnostic result section -->
  <p class="font-bold">Diagnostics result</p>

  <!-- Result label when it's been diagnosed -->
  <div v-if="isDiagnosed" :class="[diagnosticResult?.toLowerCase(), 'flex flex-row items-center gap-2']">
    <template v-if="diagnosticResult === 'Fail'"
      ><p>Failed</p>
      <Icon icon="mdi:cancel-bold"
    /></template>
    <template v-else
      ><p>Passed</p>
      <Icon icon="mdi:check"
    /></template>
  </div>

  <!-- Radio buttons when it's not -->
  <el-radio-group v-else v-model="selectedResult">
    <el-radio value="Pass" size="large">Passed</el-radio>
    <el-radio value="Fail" size="large">Failed</el-radio>
  </el-radio-group>

  <!-- Diagnosed and has files -->
  <RlAttachmentsList v-if="isDiagnosed && diagnosticAttachments.length > 0" :files="diagnosticAttachments" />

  <!-- Diagnosed but doesn't have files (could happen in case of a PSU or CB failure) -->
  <p v-else-if="isDiagnosed && diagnosticAttachments.length === 0">No files</p>

  <!-- Not diagnosed -->
  <RlUploadMultiple v-else v-model="diagnosticFiles" :limit="5" :disabled="updateDiagnosticMutation.isPending.value" />

  <!-- Disabled input with provided text when diagnosed -->
  <el-input
    v-if="isDiagnosed"
    v-model="diagnosticFailureReasons"
    disabled
    style="width: 100%"
    :rows="2"
    type="textarea"
  />

  <!-- Enabled text input otherwise -->
  <el-input
    v-else
    v-model="failureReasons"
    style="width: 100%"
    :rows="2"
    type="textarea"
    placeholder="Enter Diagnostic Notes..."
  />

  <div class="flex w-full items-center justify-between">
    <!-- Save button -->
    <el-tooltip :disabled="!validationMessage" :content="validationMessage">
      <el-button
        v-if="!isDiagnosed"
        type="primary"
        class="my-3"
        :disabled="!!validationMessage"
        :loading="updateDiagnosticMutation.isPending.value"
        @click="saveDiagnostic"
      >
        Save
      </el-button>
    </el-tooltip>

    <!-- Clear diagnostic button -->
    <el-button
      v-if="isDiagnosed"
      class="ml-auto"
      :disabled="diagnosticDeletionPending"
      :loading="diagnosticDeletionPending"
      @click="clearDiagnostic"
    >
      <Icon icon="mdi:trash-can-outline" />
    </el-button>
  </div>
</template>

<style scoped lang="scss">
.pass {
  color: var(--component-stroke-emerald);
}

.fail {
  color: var(--component-stroke-error);
}
</style>
